import { AttributionBasis, type AttributionWindow } from "./goals";
import { IntervalUnit } from "./interval";

/**
 * Default measurement window for experiments: 30 days from each profile's entry.
 */
export const DEFAULT_EXPERIMENT_MEASUREMENT_WINDOW: AttributionWindow = {
  quantity: 30,
  unit: IntervalUnit.Day,
  basis: AttributionBasis.Entry,
};

// AttributionWindow narrows `unit` to these four. Minute and year are valid
// IntervalUnit members but not valid attribution units.
const VALID_UNITS = new Set<string>([
  IntervalUnit.Hour,
  IntervalUnit.Day,
  IntervalUnit.Week,
  IntervalUnit.Month,
]);

const VALID_BASES = new Set<string>(Object.values(AttributionBasis));

/**
 * Parse an untrusted value into an AttributionWindow, or null if it isn't one.
 *
 * Returns rather than throws so callers own their error type and this module stays
 * dependency-free.
 */
export function parseAttributionWindow(
  value: unknown,
): AttributionWindow | null {
  if (typeof value !== "object" || value === null) return null;

  const { quantity, unit, basis } = value as Record<string, unknown>;

  if (
    typeof quantity !== "number" ||
    !Number.isInteger(quantity) ||
    quantity < 0
  ) {
    return null;
  }
  if (typeof unit !== "string" || !VALID_UNITS.has(unit)) return null;
  // `basis` is optional on AttributionWindow; reject only a present-but-invalid one.
  if (
    basis !== undefined &&
    (typeof basis !== "string" || !VALID_BASES.has(basis))
  ) {
    return null;
  }

  return { quantity, unit, basis } as AttributionWindow;
}
